import { useCallback, useState } from 'react';

import { Close as CloseIcon } from '@mui/icons-material';
import {
  Alert,
  Button,
  IconButton,
  Snackbar,
  Stack,
  Typography,
  type AlertColor,
} from '@mui/material';

import { type SnackbarActionAlertType } from './types';

const SnackbarAlert = ({
  actionFunction,
  actionMessage,
  anchorOrigin,
  autoHideDuration,
  message,
  onClose,
  severity,
}: SnackbarActionAlertType) => {
  const [isOpen, setIsOpen] = useState(true);

  const handleActionClick = useCallback(() => {
    setIsOpen(false);
    if (actionFunction) actionFunction();

    onClose();
  }, [actionFunction, onClose]);

  const handleToClose = useCallback(
    (_: unknown, reason?: 'clickaway' | 'escapeKeyDown' | 'timeout') => {
      if (reason === 'clickaway') return;
      setIsOpen(false);
      onClose();
    },
    [onClose],
  );

  return (
    <Snackbar
      anchorOrigin={anchorOrigin}
      autoHideDuration={autoHideDuration}
      onClose={handleToClose}
      open={isOpen}
    >
      <Alert
        action={
          <IconButton aria-label="Close" onClick={handleToClose} size="xSmall" title="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        onClose={handleToClose}
        severity={severity as AlertColor}
      >
        <Stack
          alignItems="center"
          direction="row"
          gap={1}
          justifyContent="space-between"
          width="100%"
        >
          <Typography py={1}>{message}</Typography>

          {actionMessage && (
            <Button
              aria-label="snackbar-action-button"
              color="secondary"
              data-testid="snackbar-action-button"
              onClick={handleActionClick}
              size="small"
              variant="text"
            >
              {actionMessage}
            </Button>
          )}
        </Stack>
      </Alert>
    </Snackbar>
  );
};

export default SnackbarAlert;
